import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';
import { canAccessModule, getAccessibleModules } from '../config/planFeatures';

const SubscriptionContext = createContext({});

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubscription = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/api/payments/subscriptions/active');

      if (response.data) {
        // Extract add-ons from metadata if they exist
        const addOns = response.data.extra_metadata?.addOns || [];
        
        setSubscription({
          id: response.data.id,
          plan: response.data.plan,
          status: response.data.status,
          billingPeriod: response.data.billing_period,
          startDate: response.data.start_date,
          endDate: response.data.end_date,
          currentPeriodStart: response.data.current_period_start,
          currentPeriodEnd: response.data.current_period_end,
          autoRenew: response.data.auto_renew,
          cancelledAt: response.data.cancelled_at,
          addOns: addOns,
          extraMetadata: response.data.extra_metadata || {},
        });
      }
    } catch (err) {
      // No active subscription found is okay - user might be on trial or need to subscribe
      if (err.response?.status === 404) {
        setSubscription(null);
      } else {
        setError(err.message);
        console.error('Error fetching subscription:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchSubscription();
    } else {
      setSubscription(null);
      setLoading(false);
    }
  }, [user, fetchSubscription]);

  /**
   * Check if user has access to a specific module
   */
  const hasModuleAccess = useCallback((moduleId) => {
    return canAccessModule(moduleId, subscription, user);
  }, [subscription, user]);

  /**
   * Get list of all modules user can access
   */
  const accessibleModules = useMemo(() => getAccessibleModules(subscription, user), [subscription, user]);

  /**
   * Refresh subscription data (call after payment completion)
   */
  const refreshSubscription = useCallback(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const value = useMemo(() => ({
    subscription,
    loading,
    error,
    hasModuleAccess,
    accessibleModules,
    refreshSubscription,
  }), [subscription, loading, error, hasModuleAccess, accessibleModules, refreshSubscription]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);
